/* eslint-disable no-const-assign */
import { useCallback, useEffect, useState } from "react";
import { useAppDispatch } from "hooks/hooks";
import { useSelector } from "react-redux";
import {
  getResourcesStatistics,
  selectState,
} from "../slices/ResourcesStatistics.slice";
import Loader from "components/Loader/Loader";
import { FormatNumber } from "../services/statistics.service";
import { selectState as mainSelectState } from "../slices/main.slice";
import DateSelector from "Dashboard/components/DateSelector";
import { mapMonths } from "utils/dateUtils";

export const ResourcesStatistics = () => {
  const { statistics, loading } = useSelector(selectState);
  const dispatch = useAppDispatch();
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);

  const { mainDateFrom } = useSelector(mainSelectState);

  const [quantityOption, setQuantityOption] = useState<boolean>(true);

  useEffect(() => {
    if (dateFrom != null && dateTo != null) {
      dispatch(getResourcesStatistics({ year: dateFrom?.getFullYear() }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, dateFrom, quantityOption]);

  const selectDate = useCallback((data: { dateFrom: Date; dateTo: Date }) => {
    setDateFrom(data.dateFrom);
    setDateTo(data.dateTo);
  }, []);

  const getHighest = (num: number) => {
    let highest1 = 0;
    let highest2 = 0;
    let highest3 = 0;
    let highest4 = 0;
    let highest5 = 0;
    let highest6 = 0;

    statistics?.costsByMonth.forEach((element) => {
      if (element.refuelsEstimatedCostBasedOnConsumableExemptions > highest1) {
        highest1 = element.refuelsEstimatedCostBasedOnConsumableExemptions;
      }
      if (element.refuelsEstimatedCostBasedOnCharges > highest2) {
        highest2 = element.refuelsEstimatedCostBasedOnCharges;
      }
      if (element.refuelsLitersBasedOnConsumableExemptions > highest5) {
        highest5 = element.refuelsLitersBasedOnConsumableExemptions;
      }
      if (element.refuelsEstimatedLitersBasedOnCharges > highest6) {
        highest6 = element.refuelsEstimatedLitersBasedOnCharges;
      }
      if (element.tachographsCost > highest3) {
        highest3 = element.tachographsCost;
      }
      if (element.registrationsCost > highest3) {
        highest3 = element.registrationsCost;
      }
      Object.values(element.nonFuel).forEach((val) => {
        if (val > highest4) {
          highest4 = val;
        }
      });
    });

    if (num === 1) {
      return highest1;
    } else if (num === 2) {
      return highest2;
    } else if (num === 3) {
      return highest3;
    } else if (num === 5) {
      return highest5;
    } else if (num === 6) {
      return highest6;
    } else {
      return highest4;
    }
  };

  const getHeight = (num: number, graphnum: number) => {
    if (num === 0) {
      return 0;
    }
    if (num) {
      const result = 100 / (getHighest(graphnum) / num);
      if (Math.round(result) === 0 && num > 0) {
        return 1;
      }
      return Math.round(result);
    }
  };

  return (
    <div className="consumablesDiv">
      <div className="header">
        Potrošnja potrošnih materijala i usluga vezano za resurse
      </div>
      <div className="resourcesSelector">
        <div className="dateSelector">
          <DateSelector
            selectedYear={mainDateFrom?.getFullYear()}
            showMonth={false}
            lastYearNumbers={5}
            selectDate={selectDate}
          />
        </div>
        <div className="priceSelector">
          <span>
            <input
              type="radio"
              name="price"
              onClick={() => setQuantityOption(true)}
              checked={quantityOption}
            />{" "}
            količina
          </span>
          <span>
            <input
              type="radio"
              name="price"
              onClick={() => setQuantityOption(false)}
            />{" "}
            cena
          </span>
        </div>
      </div>
      <div className="graphs carnets loading-container">
        {loading && <Loader />}
        <div className="graphs-header carnets-header">
          <span>
            <div className="color7"></div>Gorivo - procena po zaduženju
            kilometara ili radnih sati
          </span>
        </div>
        <div className="graphs-content loading-container">
          {statistics?.costsByMonth &&
            statistics?.costsByMonth.map((s) => (
              <div className="monthDiv">
                <p>{mapMonths(s.month)}</p>
                {quantityOption && (
                  <>
                    <div
                      className={`color7 resourceGraph-${getHeight(
                        s.refuelsLitersBasedOnConsumableExemptions,
                        5
                      )}`}
                    ></div>

                    <p>{FormatNumber(s.refuelsLitersBasedOnConsumableExemptions)} l</p>
                  </>
                )}
                {!quantityOption && (
                  <>
                    <div
                      className={`color7 resourceGraph-${getHeight(
                        s.refuelsEstimatedCostBasedOnConsumableExemptions,
                        1
                      )}`}
                    ></div>

                    <p>{FormatNumber(s.refuelsEstimatedCostBasedOnConsumableExemptions)} rsd</p>
                  </>
                )}
              </div>
            ))}
        </div>
      </div>
      <div className="graphs carnets">
        <div className="graphs-header carnets-header">
          <span>
            <div className="color7"></div>Gorivo - egzaktne izdate kolicine
          </span>
        </div>
        <div className="graphs-content loading-container">
          {statistics?.costsByMonth &&
            statistics.costsByMonth.map((s) => (
              <div className="monthDiv">
                <p>{mapMonths(s.month)}</p>
                {quantityOption && (
                  <>
                    <div
                      className={`color7 resourceGraph-${getHeight(
                        s.refuelsEstimatedLitersBasedOnCharges,
                        6
                      )}`}
                    ></div>
                    <p>{FormatNumber(s.refuelsEstimatedLitersBasedOnCharges)} l</p>
                  </>
                )}
                {!quantityOption && (
                  <>
                    <div
                      className={`color7 resourceGraph-${getHeight(
                        s.refuelsEstimatedCostBasedOnCharges,
                        2
                      )}`}
                    ></div>
                    <p>{FormatNumber(s.refuelsEstimatedCostBasedOnCharges)} rsd</p>
                  </>
                )}
              </div>
            ))}
        </div>
      </div>
      <div className="graphs carnets">
        <div className="graphs-header carnets-header">
          <span>
            <div className="color7"></div>Registracije
          </span>
          <span>
            <div className="color6"></div>Tahografi
          </span>
        </div>
        <div className="graphs-content loading-container">
          {statistics?.costsByMonth &&
            statistics.costsByMonth.map((s) => (
              <div className="monthDiv">
                <p>{mapMonths(s.month)}</p>
                <div className="dualGraphs">
                  <span
                    className={`carnetGraph-${getHeight(
                      s.registrationsCost,
                      3
                    )}`}
                  >
                    <p>{FormatNumber(s.registrationsCost)}</p>
                    <div className="color7"></div>
                  </span>
                  <span
                    className={`carnetGraph-${getHeight(s.tachographsCost, 3)}`}
                  >
                    <p>{FormatNumber(s.tachographsCost)}</p>
                    <div className="color6"></div>
                  </span>
                </div>
              </div>
            ))}
        </div>
      </div>
      <div className="salaries">
        <div className="salariesGraphs">
          {statistics?.costsByMonth &&
            statistics?.costsByMonth.map((s) => (
              <div className="horizontalGraph">
                <p>{mapMonths(s.month)}</p>
                <div className="horizontalGraphContent">
                  <span
                    className={`horizontalGraphWidth-${getHeight(
                      s.nonFuel.lubricantCosts,
                      4
                    )}`}
                  >
                    <div className="color1"></div>
                    <p>{FormatNumber(s.nonFuel.lubricantCosts)}</p>
                  </span>
                  <span
                    className={`horizontalGraphWidth-${getHeight(
                      s.nonFuel.filterCosts,
                      4
                    )}`}
                  >
                    <div className="color2"></div>
                    <p>{FormatNumber(s.nonFuel.filterCosts)}</p>
                  </span>
                  <span
                    className={`horizontalGraphWidth-${getHeight(
                      s.nonFuel.replacementPartCosts,
                      4
                    )}`}
                  >
                    <div className="color3"></div>
                    <p>{FormatNumber(s.nonFuel.replacementPartCosts)}</p>
                  </span>
                  <span
                    className={`horizontalGraphWidth-${getHeight(
                      s.nonFuel.tyreCosts,
                      4
                    )}`}
                  >
                    <div className="color4"></div>
                    <p>{FormatNumber(s.nonFuel.tyreCosts)}</p>
                  </span>
                  <span
                    className={`horizontalGraphWidth-${getHeight(
                      s.nonFuel.otherCosts,
                      4
                    )}`}
                  >
                    <div className="color12"></div>
                    <p>{FormatNumber(s.nonFuel.otherCosts)}</p>
                  </span>
                  <span
                    className={`horizontalGraphWidth-${getHeight(
                      s.nonFuel.serviceCosts,
                      4
                    )}`}
                  >
                    <div className="color9"></div>
                    <p>{FormatNumber(s.nonFuel.serviceCosts)}</p>
                  </span>
                </div>
              </div>
            ))}
        </div>
        <div className="salariesGraphsInfo">
          <span>
            <div className="color1"></div>Maziva
          </span>
          <span>
            <div className="color2"></div>Filteri
          </span>
          <span>
            <div className="color3"></div>Rezervni delovi
          </span>
          <span>
            <div className="color4"></div>Gume
          </span>
          <span>
            <div className="color12"></div>Ostalo
          </span>
          <span>
            <div className="color9"></div>Usluge
          </span>
        </div>
      </div>
    </div>
  );
};
